import React from "react";
import { useNavigate } from "react-router-dom";
import { goToFeedMatchTab } from "@/helpers/matchTabRedirect";
import { foundThrough } from "@/helpers/resultsCopy";
import "./GitchaPageLeadNotification.scss";

export const GITCHA_PAGE_LEAD_SUBJECT_TYPE = 41;

const ICON_PATHS = {
  message: "M4 5h16v11H9l-5 4z",
  email: "M4 5h16v11H9l-5 4z",
  buyer_form: "M7 3h7l4 4v14H7zM14 3v4h4M10 12h5M10 16h5",
  demand_check: "M4 11l8-7 8 7v9H4zM9 20v-6h6v6",
  call: "M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a1 1 0 0 1-1 1A16 16 0 0 1 4 5a1 1 0 0 1 1-1z",
  text: "M4 5h16v11H9l-5 4zM8 10h8M8 13h5",
  website: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z",
  social: "M10 14l4-4M8.5 11.5l-2 2a3 3 0 0 0 4.2 4.2l2-2M15.5 12.5l2-2a3 3 0 0 0-4.2-4.2l-2 2",
};

const LINK_NAMES = {
  website: "your website",
  facebook: "your Facebook",
  instagram: "your Instagram",
  linkedin: "your LinkedIn",
  youtube: "your YouTube",
  tiktok: "your TikTok",
  x: "your X profile",
};

const firstNameOf = (name) => String(name || "").trim().split(/\s+/)[0] || "";

export const leadContactLinks = (notification) => {
  const first = firstNameOf(notification?.ContactName);
  const rawEmail = String(notification?.ContactEmail || "").trim();
  const email = /^[^\s@?&,;<>]+@[^\s@?&,;<>]+\.[^\s@?&,;<>]+$/.test(rawEmail) ? rawEmail : "";
  const phone = String(notification?.ContactPhone || "").replace(/[^\d+]/g, "");
  return [
    email && { label: first ? `Email ${first}` : "Email", href: `mailto:${email}` },
    phone && { label: first ? `Call ${first}` : "Call", href: `tel:${phone}` },
  ].filter(Boolean);
};

const messagesOf = (count) => (count > 1 ? `${count} messages` : "a message");

export const leadNotificationCopy = (notification, count = 1) => {
  const type = notification?.ContactType;
  const who = String(notification?.ContactName || "").trim() || "Someone";
  const reachable = leadContactLinks(notification).length > 0;
  const inEmail = reachable ? "" : " Their details are in your email.";
  const fromShowcase = notification?.Source === "showcase";
  const from = fromShowcase ? "from your website" : "from your Gitcha bio";
  const origin = fromShowcase ? "" : foundThrough(notification?.Channel, notification?.Source);
  const withOrigin = (text) => (origin ? `${text} ${origin}` : text);
  if (type === "demand_check") {
    return {
      text: `${who} checked buyer demand for their home on your brokerage website and confirmed their email.${inEmail}`,
      action: null,
    };
  }
  if (type === "buyer_form" && notification?.Intent === "sell") {
    return notification?.Prospect_Inv_Id
      ? {
          text: withOrigin(`${who} asked you about selling their home ${from}. Their home is in My Properties.${inEmail}`),
          action: "See demand insights",
          invId: notification.Prospect_Inv_Id,
          keepLinks: true,
        }
      : {
          text: withOrigin(`${who} asked you about selling their home ${from}.${inEmail}`),
          action: null,
        };
  }
  if (type === "buyer_form") {
    return notification?.Draft_Want_ad_Id
      ? {
          text: withOrigin(`${who} filled out your Buyer Listing Form ${from}. Their Buyer Want-Listing is a draft in Ready to review.`),
          action: "Review the draft",
        }
      : {
          text: withOrigin(`${who} filled out your Buyer Listing Form ${from}.${inEmail}`),
          action: null,
        };
  }
  if (type === "message") {
    return {
      text: withOrigin(`${who} sent you ${messagesOf(count)} ${fromShowcase ? from : "on Gitcha"}. They are a home shopper, not an agent.`),
      action: notification?.ContactUserId ? "Open the conversation" : null,
      threadUserId: notification?.ContactUserId || null,
      keepLinks: false,
    };
  }
    if (type === "email") {
    return {
      text: withOrigin(`${who} sent you ${messagesOf(count)} ${from}.${reachable ? "" : " Reply from your email."}`),
      action: null,
    };
  }
  if (type === "website" || type === "social") {
    const where = LINK_NAMES[notification?.Intent] || "your link";
    const verb = type === "website" ? "went to" : "opened";
    return { text: withOrigin(`Someone ${verb} ${where} from your Gitcha bio.`), action: null };
  }
  const label = type === "text" ? "Text" : "Call";
  return { text: withOrigin(`Someone tapped ${label} on your Gitcha bio.`), action: null };
};

const TrashIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 6.5h16M9.5 6.5V4.5h5v2M6.5 6.5l1 13h9l1-13M10.5 10v6.5M13.5 10v6.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const GitchaPageLeadNotification = ({ notification, timeLabel, onOpenDraft, onDelete, count = 1 }) => {
  const copy = leadNotificationCopy(notification, count);
  const navigate = useNavigate();
  const links = copy.action && !copy.keepLinks ? [] : leadContactLinks(notification);
  const actionable = Boolean(copy.action) || links.length > 0;
  return (
    <div className={`notification-sec position-relative notify-info-bg gpln${actionable ? "" : " is-static"}`}>
      <button
        type="button"
        className="btn notif-delete-btn"
        aria-label="Dismiss"
        onClick={(event) => {
          event.stopPropagation();
          onDelete(notification);
        }}
      >
        <TrashIcon />
      </button>
      <div className="outer-property-wrap pl-20 pr-20 pt-12 pb-12">
        <div className="d-flex mb-0 flex-grow-1 align-items-center">
          <div className="gpln-mark flex-shrink-0" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d={ICON_PATHS[notification?.ContactType] || ICON_PATHS.message}
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="introduction-content flex-grow-1 ml-10 pe-0 ps-0 pb-0">
            <p className="mb-0 notification-head">
              <span className="f-bold">{copy.text}</span>{" "}
              {copy.action ? (
                <button
                  type="button"
                  className="gpln-action"
                  onClick={() =>
                    copy.threadUserId
                      ? navigate(`/direct-messages?id=${copy.threadUserId}`)
                      : copy.invId
                        ? goToFeedMatchTab(navigate, copy.invId)
                        : onOpenDraft(notification.Draft_Want_ad_Id)
                  }
                >
                  {copy.action}
                </button>
              ) : null}
              {links.map((link) => (
                <a
                  key={link.href}
                  className="gpln-action gpln-link"
                  href={link.href}
                  onClick={(event) => event.stopPropagation()}
                >
                  {link.label}
                </a>
              ))}
              {timeLabel ? <span className="time-stamp">{timeLabel}</span> : null}
            </p>
          </div>
        </div>
        {notification?.Read_Status == 1 ? (
          <div className="dot-indicator dot-bg-red align-self-center mt-0" />
        ) : null}
      </div>
    </div>
  );
};

export default GitchaPageLeadNotification;
